import { useEffect, useState } from 'react'
import { useNav } from '../lib/nav'
import * as Sentry from '@sentry/react'
import { Page, TopBar } from '../components/Shell'
import { useCountUp } from '../lib/motion'
import { KV } from '../components/Controls'
import { SignSheet } from '../components/SignSheet'
import { useStore } from '../state/store'
import { createSale } from '../lib/api'
import { walletLabel, belowFloor, fmt2, fmtC, lineCents, pct, taxOnLines, taxOptional, TENDER_LABEL, type Employee } from '../lib/types'
import { partCents, splitAmounts } from './SellPay'

export interface SaleDoneState { sale_id: string; sale_no: number; total_cents: number; method: string; by: Employee; approved: Employee | null; skus: string[]; stock?: Record<string, string> }

export function SellReview() {
  const nav = useNav()
  const { sale, customers, resetSale, refresh, tax } = useStore()
  const [sheet, setSheet] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const empty = sale.lines.length === 0
  useEffect(() => { if (empty && !busy) nav('/sell', { replace: true }) }, [empty, busy, nav])
  const subtotal = sale.lines.reduce((a, l) => a + lineCents(l), 0)
  const noTax = taxOptional(sale) && sale.taxMode === 'none'
  const taxC = noTax ? 0 : taxOnLines(sale.lines, tax)
  const total = subtotal + taxC
  const totalShown = Math.round(useCountUp(total))   // motion: the drawn total rolls; `total` is what the sale sends
  const { cardCents } = splitAmounts(sale.parts, total)
  const partLine = (p: typeof sale.parts[number]) => `${TENDER_LABEL[p.method]}${p.method === 'crypto' ? ' ' + walletLabel(p.coin, p.network) : ''} ${fmt2(p.method === 'card' ? cardCents : partCents(p))}`
  const needsOwner = sale.lines.some(belowFloor)
  const cust = customers.find((c) => c.id === sale.customerId)
  const custTxt = cust ? `${cust.first_name} ${cust.last_name}`.trim() : 'Not picked'
  const pay = sale.method === 'zelle' ? 'Zelle' : sale.method === 'crypto' ? `Crypto, ${walletLabel(sale.coin, sale.network)}` : sale.method === 'card' ? 'Card, charged in Shopify POS' : sale.method === 'paypal' ? 'PayPal' : sale.method === 'split' ? `Split: ${sale.parts.map(partLine).join(' + ')}` : 'Cash'
  const methodShort = sale.method === 'crypto' ? `Crypto ${walletLabel(sale.coin, sale.network)}` : sale.method === 'split' ? 'Split' : (TENDER_LABEL[sale.method] ?? 'Cash')

  const confirm = async (e: Employee) => {
    setBusy(true); setErr('')
    try {
      const r = await createSale(sale, e.employee_id, needsOwner ? e.employee_id : null, cardCents)
      const state: SaleDoneState = { sale_id: r.sale_id, sale_no: r.sale_no, total_cents: r.total_cents, method: methodShort, by: e, approved: needsOwner ? e : null, skus: sale.lines.map((l) => l.piece.sku || l.piece.barcode) }
      refresh().catch(() => {})
      // a pure card sale waits on POS; anything else (including a split with a card part) is collected part by part
      nav(sale.method === 'card' ? '/sell/card' : `/sell/collect/${r.sale_id}`, { state, replace: true })
      setTimeout(resetSale, 0)
    } catch (ex) {
      Sentry.captureException(ex); setErr((ex as Error).message || 'Could not record the sale'); setSheet(false)
    } finally { setTimeout(() => setBusy(false), 50) }
  }

  return (
    <Page bar={<TopBar back="Payment" backTo="/sell/pay" title="Review sale" />}>
      {needsOwner && <div className="banner">A piece is priced below its floor. Shane or Masih has to approve with their ID to finish.</div>}
      <div className="hint">Nothing leaves stock until the payment is confirmed on the next screen.</div>
      {err && <div className="banner" style={{ background: '#d70015' }}>{err}. Nothing was recorded.</div>}
      <div className="section-title">Pieces</div>
      <div className="card review-rows">
        {sale.lines.map((l) => <KV key={l.piece.variant_id} k={l.piece.title} v={<span style={{ color: belowFloor(l) ? '#d70015' : undefined, fontWeight: 600 }}>{fmtC(lineCents(l))}{belowFloor(l) ? ' · below floor' : ''}</span>} />)}
      </div>
      <div className="section-title">Payment</div>
      <div className="card review-rows">
        <KV k="Method" v={pay} />
        <KV k="Pieces" v={fmt2(subtotal)} />
        <KV k={noTax ? 'Sales tax' : `Sales tax (${pct(tax.rate)})`} v={noTax ? 'None' : sale.method === 'card' ? `${fmt2(taxC)} · Shopify POS adds it at Charge` : fmt2(taxC)} />
        <KV k="Customer" v={custTxt} />
      </div>
      <div className="total"><span>Total owed</span><span>{fmt2(totalShown)}</span></div>
      <button type="button" className="cta" onClick={() => setSheet(true)}><span>{needsOwner ? 'Owner approves and records' : sale.method === 'card' ? 'Sign and send to POS' : 'Sign and collect payment'}</span><span>{fmt2(total)}</span></button>
      {sheet && <SignSheet title={needsOwner ? 'Owner approval' : 'Sign with your ID'} ownerOnly={needsOwner} onConfirm={confirm} onClose={() => !busy && setSheet(false)} busy={busy} />}
    </Page>
  )
}
